import {
  getWeights, setWeights, getAllRecords, createBackupSnapshot, listBackups, restoreBackup,
  replaceAllRecords,
} from '../db/indexeddb';
import { DIMENSIONS, type Dimension, type Weights } from '../lib/types';
import { recordsToCsv, csvToRows, validateImportRows, downloadFile } from '../lib/importExport';
import { getApiConfig, setApiConfig, flushOutbox, getCurrentStatus } from '../lib/sync';
import { showToast, formatDateTime } from './shared';

const DIMENSION_LABELS: Record<Dimension, string> = {
  vision_alignment: 'Vision alignment',
  impact: 'Impact',
  revenue_potential: 'Revenue potential',
  market_window: 'Market window',
  time_efficiency: 'Time / resource efficiency',
  leverage: 'Leverage / compounding',
  evidence_readiness: 'Evidence / readiness',
};

export async function renderSettings(container: HTMLElement): Promise<void> {
  const weights = await getWeights();
  const apiCfg = await getApiConfig();
  const backups = await listBackups();

  container.innerHTML = `
    <h2 style="font-size:19px;">Settings</h2>

    <div class="section-title">Ranking Weights <span style="font-size:11px; color:var(--text-faint); text-transform:none; letter-spacing:0;">must total 100%</span></div>
    <div class="card">
      ${DIMENSIONS.map((d) => `
        <div class="score-row">
          <div class="score-label">${DIMENSION_LABELS[d]}</div>
          <input type="number" min="0" max="100" step="1" id="w-${d}" value="${Math.round(weights[d] * 100)}" style="width:70px; text-align:right;" />
          <span style="width:14px;">%</span>
        </div>
      `).join('')}
      <div id="w-total" style="margin-top:10px; font-size:13px; color:var(--text-dim);"></div>
      <button class="primary" id="w-save" style="width:100%; margin-top:10px;">Save Weights</button>
    </div>

    <div class="section-title">Sync to Agent Elohim Backend</div>
    <div class="card">
      <p class="field-hint" style="margin-bottom:10px;">This app works fully offline without this. When set, changes queue locally and sync automatically when online.</p>
      <label>API base URL</label>
      <input id="s-base" value="${apiCfg?.baseUrl ?? ''}" placeholder="https://gvm.elohimnetwork.com" />
      <label>API token</label>
      <input id="s-token" value="${apiCfg?.token ?? ''}" placeholder="Bearer token" type="password" />
      <button class="primary" id="s-save" style="width:100%; margin-top:10px;">Save & Sync Now</button>
      <div class="field-hint" style="margin-top:8px;">Current status: <b id="s-status">${getCurrentStatus()}</b></div>
    </div>

    <div class="section-title">Export</div>
    <div class="card" style="display:flex; gap:10px;">
      <button id="ex-json" style="flex:1;">Export JSON</button>
      <button id="ex-csv" style="flex:1;">Export CSV</button>
    </div>

    <div class="section-title">Import</div>
    <div class="card">
      <p class="field-hint" style="margin-bottom:10px;">A backup snapshot is taken automatically before any import. Invalid rows block the whole import — nothing partial gets written.</p>
      <label>JSON or CSV file</label>
      <input type="file" id="im-file" accept=".json,.csv" />
      <div id="im-result" style="margin-top:8px; font-size:13px;"></div>
    </div>

    <div class="section-title">Backups <span style="font-size:11px; color:var(--text-faint); text-transform:none; letter-spacing:0;">${backups.length} saved</span></div>
    <div class="card">
      <button id="bk-create" style="width:100%; margin-bottom:10px;">Create Backup Now</button>
      ${backups.length ? backups.slice(0, 10).map((b) => `
        <div style="display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:1px solid var(--border); font-size:13px;">
          <div>
            <div>${formatDateTime(b.createdAt)}</div>
            <div style="color:var(--text-faint); font-size:11px;">${b.reason} · ${b.records.length} records</div>
          </div>
          <button data-restore="${b.id}" style="min-height:32px; padding:0 12px; font-size:12px;">Restore</button>
        </div>
      `).join('') : '<div class="empty">No backups yet.</div>'}
    </div>
  `;

  // ---- weights ----
  function computeTotal(): number {
    return DIMENSIONS.reduce((sum, d) => sum + Number((container.querySelector(`#w-${d}`) as HTMLInputElement).value || 0), 0);
  }
  function refreshTotal() {
    const total = computeTotal();
    const el = container.querySelector('#w-total') as HTMLElement;
    el.textContent = `Total: ${total}%`;
    el.style.color = total === 100 ? 'var(--status-completed)' : 'var(--status-blocked)';
  }
  DIMENSIONS.forEach((d) => {
    container.querySelector(`#w-${d}`)?.addEventListener('input', refreshTotal);
  });
  refreshTotal();

  container.querySelector('#w-save')?.addEventListener('click', async () => {
    const total = computeTotal();
    if (total !== 100) { showToast(`Weights must total 100% (currently ${total}%)`, 'error'); return; }
    const next: Weights = {} as Weights;
    DIMENSIONS.forEach((d) => {
      next[d] = Number((container.querySelector(`#w-${d}`) as HTMLInputElement).value) / 100;
    });
    await setWeights(next);
    showToast('Weights saved — all scores recalculated');
  });

  // ---- sync config ----
  container.querySelector('#s-save')?.addEventListener('click', async () => {
    const base = (container.querySelector('#s-base') as HTMLInputElement).value.trim();
    const token = (container.querySelector('#s-token') as HTMLInputElement).value.trim();
    if (!base || !token) { showToast('Base URL and token both required to sync', 'error'); return; }
    await setApiConfig(base, token);
    showToast('Saved. Syncing…');
    await flushOutbox();
    (container.querySelector('#s-status') as HTMLElement).textContent = getCurrentStatus();
  });

  // ---- export ----
  container.querySelector('#ex-json')?.addEventListener('click', async () => {
    const records = await getAllRecords(true);
    downloadFile(`gvm_export_${dateStamp()}.json`, JSON.stringify({ exported_at: new Date().toISOString(), records }, null, 2), 'application/json');
  });
  container.querySelector('#ex-csv')?.addEventListener('click', async () => {
    const records = await getAllRecords(true);
    downloadFile(`gvm_export_${dateStamp()}.csv`, recordsToCsv(records), 'text/csv');
  });

  // ---- import ----
  container.querySelector('#im-file')?.addEventListener('change', async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const text = await file.text();
    const resultEl = container.querySelector('#im-result') as HTMLElement;

    let rows: Array<Record<string, unknown>>;
    try {
      if (file.name.endsWith('.json')) {
        const parsed = JSON.parse(text);
        rows = Array.isArray(parsed) ? parsed : parsed.records;
        if (!Array.isArray(rows)) throw new Error('Expected an array of records or {records: [...]}');
      } else {
        rows = csvToRows(text);
      }
    } catch (err) {
      resultEl.innerHTML = `<span style="color:var(--status-blocked);">Could not parse file: ${(err as Error).message}</span>`;
      return;
    }

    const { valid, errors } = validateImportRows(rows);
    if (errors.length) {
      resultEl.innerHTML = `<span style="color:var(--status-blocked);">${errors.length} row(s) failed validation — import blocked:</span><br>` +
        errors.slice(0, 10).map((e) => `<div style="color:var(--text-faint);">${e}</div>`).join('');
      return;
    }

    await createBackupSnapshot(`pre-import (${file.name})`);
    const { inserted, updated } = await replaceAllRecords(valid);
    resultEl.innerHTML = `<span style="color:var(--status-completed);">Imported: ${inserted} new, ${updated} updated. Backup created first.</span>`;
    showToast('Import complete');
    input.value = '';
  });

  // ---- backups ----
  container.querySelector('#bk-create')?.addEventListener('click', async () => {
    await createBackupSnapshot('manual');
    showToast('Backup created');
    void renderSettings(container);
  });
  container.querySelectorAll('[data-restore]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = (btn as HTMLElement).dataset.restore!;
      if (!window.confirm('Restore this backup? Current records will be replaced with the backup\'s contents.')) return;
      await createBackupSnapshot('pre-restore safety snapshot');
      await restoreBackup(id);
      showToast('Backup restored');
      void renderSettings(container);
    });
  });
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}
